import React, { useEffect, useState } from 'react';
import { Plus, Save } from 'lucide-react';
import { settingsApi } from '../api/settings';
import api from '../api/client';
import { useToast } from '../hooks/useToast';
import RoleManagement from './RoleManagement';
import RepairTermsTab from './RepairTermsTab';

const Settings: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab]             = useState<'company'|'branches'|'locations'|'users'|'roles'|'terms'>('company');
  const [company, setCompany]     = useState<any>(null);
  const [branches, setBranches]   = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [users, setUsers]         = useState<any[]>([]);
  const [roles, setRoles]         = useState<any[]>([]);
  const [cashAccounts, setCashAccounts] = useState<any[]>([]);
  const [branchAcctSel, setBranchAcctSel] = useState<number[]>([]);
  const adminLoadBranchAccts = (bid:number) => { api.get('/branch-accounts/'+bid).then(r => { const d = r.data?.data ?? r.data; setBranchAcctSel(Array.isArray(d) ? d.map((a:any)=>Number(a.id)) : []); }).catch(() => setBranchAcctSel([])); };
  const [saving, setSaving]       = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [branchForm, setBranchForm]   = useState({ name:'', code:'', address:'', phone:'', email:'' });
  const [locationForm, setLocationForm] = useState({ name:'', branch_id:'' });
  const [userForm, setUserForm]   = useState({ name:'', email:'', password:'', role_id:'', scope:'branch', branch_id:'' });

  const load = () => {
    settingsApi.getCompany().then(r => setCompany(r.data.data));
    settingsApi.getBranches().then(r => setBranches(r.data.data));
    settingsApi.getLocations().then(r => setLocations(r.data.data));
    settingsApi.getUsers().then(r => setUsers(r.data.data));
    settingsApi.getRoles().then(r => setRoles(r.data.data));
    api.get('/purchase/cash-accounts').then(r => { const d = r.data?.data ?? r.data; setCashAccounts(Array.isArray(d) ? d : []); }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleUpdateCompany = async () => {
    setSaving(true);
    try {
      await settingsApi.updateCompany({ name:company.name, email:company.email, phone:company.phone, address:company.address, tax_number:company.tax_number, timezone:company.timezone });
      showToast('Company updated!');
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleBranch = async () => {
    if (!branchForm.name || !branchForm.code) { showToast('Name and code required', 'error'); return; }
    try {
      await settingsApi.createBranch(branchForm);
      showToast('Branch created!'); setShowForm(false); setBranchForm({ name:'', code:'', address:'', phone:'', email:'' }); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleLocation = async () => {
    if (!locationForm.name || !locationForm.branch_id) { showToast('Name and branch required', 'error'); return; }
    try {
      await settingsApi.createLocation({ ...locationForm, branch_id: Number(locationForm.branch_id) });
      showToast('Location created!'); setShowForm(false); setLocationForm({ name:'', branch_id:'' }); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password || !userForm.role_id) { showToast('All fields required', 'error'); return; }
    if (userForm.scope === 'branch' && !userForm.branch_id) { showToast('Branch is required for branch-scoped users', 'error'); return; }
    try {
      await settingsApi.createUser({ ...userForm, role_id: Number(userForm.role_id), branch_id: userForm.branch_id ? Number(userForm.branch_id) : null });
      showToast('User created!'); setShowForm(false); setUserForm({ name:'', email:'', password:'', role_id:'', scope:'branch', branch_id:'' }); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const tabs = [
    { key:'company',   label:'Company' },
    { key:'branches',  label:'Branches' },
    { key:'locations', label:'Locations' },
    { key:'users',     label:'Users' },
    { key:'roles',     label:'Roles' },
    { key:'terms',     label:'Repair Terms' },
  ];

  const [editBranch, setEditBranch] = useState<any>(null);
  const [editBranchForm, setEditBranchForm] = useState<any>({});
  const [editUser, setEditUser] = useState<any>(null);
  const [editUserForm, setEditUserForm] = useState<any>({ name:'', role_id:'', scope:'branch', branch_id:'', phone:'' });
  const [pwUser, setPwUser] = useState<any>(null);
  const [pwValue, setPwValue] = useState('');
  const openEditUser = (u: any) => {
    setEditUser(u);
    setEditUserForm({ name: u.name || '', role_id: String(u.role_id || ''), scope: u.scope || 'branch', branch_id: String(u.branch_id || ''), phone: u.phone || '' });
  };
  const handleSaveUser = async () => {
    if (!editUser) return;
    if (editUserForm.scope === 'branch' && !editUserForm.branch_id) { showToast('Branch required for branch scope', 'error'); return; }
    try {
      await settingsApi.updateUser(editUser.id, { ...editUserForm, role_id: Number(editUserForm.role_id), branch_id: editUserForm.branch_id ? Number(editUserForm.branch_id) : null });
      showToast('User updated'); setEditUser(null); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };
  const handleToggleUser = async (u: any) => {
    try { await settingsApi.toggleUser(u.id); showToast(u.is_active ? 'User deactivated' : 'User activated'); load(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };
  const handleResetPw = async () => {
    if (!pwUser) return;
    if (pwValue.length < 8) { showToast('Password must be at least 8 characters', 'error'); return; }
    try { await settingsApi.resetUserPassword(pwUser.id, pwValue); showToast('Password reset'); setPwUser(null); setPwValue(''); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };
  const [editLocation, setEditLocation] = useState<any>(null);
  const [editLocationForm, setEditLocationForm] = useState<any>({});
  const handleToggleBranch = async (id:number, isActive:any) => {
    try { await api.patch('/settings/branches/'+id,{}); showToast(Number(isActive)===1?'Disabled':'Enabled'); load(); }
    catch { showToast('Failed','error'); }
  };
  const handleDeleteBranch = async (id:number) => {
    if (!window.confirm('Delete this branch?')) return;
    try { await api.delete('/settings/branches/'+id); showToast('Deleted'); load(); }
    catch (err:any) { showToast(err.response?.data?.message||'Cannot delete','error'); }
  };
  const handleSaveBranch = async () => {
    if (!editBranch) return;

    try {
      await api.put('/settings/branches/'+editBranch.id, { name: editBranchForm.name, address: editBranchForm.address });
      await api.post('/settings/branches/'+editBranch.id+'/accounts', { account_ids: branchAcctSel });
      showToast('Updated'); setEditBranch(null); load();
    }
    catch (err:any) { showToast(err.response?.data?.message||'Failed','error'); }
  };
  const handleToggleLocation = async (id:number, isActive:any) => {
    try { await api.patch('/settings/locations/'+id,{}); showToast(Number(isActive)===1?'Disabled':'Enabled'); load(); }
    catch { showToast('Failed','error'); }
  };
  const handleDeleteLocation = async (id:number) => {
    if (!window.confirm('Delete this location?')) return;
    try { await api.delete('/settings/locations/'+id); showToast('Deleted'); load(); }
    catch (err:any) { showToast(err.response?.data?.message||'Cannot delete','error'); }
  };
  const handleSaveLocation = async () => {
    if (!editLocation) return;
    try { await api.put('/settings/locations/'+editLocation.id, editLocationForm); showToast('Updated'); setEditLocation(null); load(); }
    catch (err:any) { showToast(err.response?.data?.message||'Failed','error'); }
  };
  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        {tab !== 'company' && tab !== 'terms' && <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Add {tab === 'branches' ? 'Branch' : tab === 'locations' ? 'Location' : 'User'}</button>}
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:'2px solid var(--bdr)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key as any); setShowForm(false); }} style={{ padding:'8px 16px', fontSize:12, fontWeight:600, border:'none', background:'none', cursor:'pointer', borderBottom: tab===t.key ? '2px solid var(--blue)' : '2px solid transparent', color: tab===t.key ? 'var(--blue)' : 'var(--tx3)', marginBottom:'-2px' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'company' && company && (
        <div className="card">
          <div className="card-header"><span>Company Information</span></div>
          <div className="card-body">
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Company Name *</label><input className="form-control" value={company.name||''} onChange={e => setCompany((c:any)=>({...c,name:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Tax Number</label><input className="form-control" value={company.tax_number||''} onChange={e => setCompany((c:any)=>({...c,tax_number:e.target.value}))} /></div>
            </div>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={company.email||''} onChange={e => setCompany((c:any)=>({...c,email:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={company.phone||''} onChange={e => setCompany((c:any)=>({...c,phone:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Address</label><textarea className="form-control" rows={3} value={company.address||''} onChange={e => setCompany((c:any)=>({...c,address:e.target.value}))} /></div>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Timezone</label>
                <select className="form-control" value={company.timezone||'UTC'} onChange={e => setCompany((c:any)=>({...c,timezone:e.target.value}))}>
                  <option value="UTC">UTC</option>
                  <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                  <option value="Africa/Mogadishu">Africa/Mogadishu</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Plan</label><input className="form-control" value={company.plan||''} disabled style={{background:'var(--sur2)',color:'var(--tx3)'}} /></div>
            </div>
            <button className="btn btn-primary" onClick={handleUpdateCompany} disabled={saving}><Save size={13}/> {saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      )}

      {tab === 'branches' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom:12 }}>
              <div className="card-header"><span>New Branch</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
              <div className="card-body">
                <div className="form-grid-3">
                  <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={branchForm.name} onChange={e => setBranchForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Bosaso Branch" /></div>
                  <div className="form-group"><label className="form-label">Code *</label><input className="form-control mono" value={branchForm.code} onChange={e => setBranchForm(f=>({...f,code:e.target.value.toUpperCase()}))} placeholder="e.g. BSO" /></div>
                  <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={branchForm.phone} onChange={e => setBranchForm(f=>({...f,phone:e.target.value}))} /></div>
                </div>
                <div className="form-group"><label className="form-label">Address</label><input className="form-control" value={branchForm.address} onChange={e => setBranchForm(f=>({...f,address:e.target.value}))} /></div>
                <button className="btn btn-primary" onClick={handleBranch}>Save Branch</button>
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Code</th><th>Phone</th><th>Address</th><th>Status</th></tr></thead>
              <tbody>
                {branches.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No branches yet.</p></div></td></tr>
                : branches.map(b => (
                  <tr key={b.id}>
                    <td style={{fontWeight:600}}>{b.name}</td>
                    <td className="td-code">{b.code}</td>
                    <td style={{color:'var(--tx3)'}}>{b.phone || '-'}</td>
                    <td style={{color:'var(--tx3)',fontSize:11}}>{b.address || '-'}</td>
                    <td><span className={`badge ${Number(b.is_active)===1 ? 'badge-green' : 'badge-gray'}`}>{Number(b.is_active)===1 ? 'Active' : 'Inactive'}</span></td>
                    <td style={{display:'flex',gap:4}}>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditBranch(b); setEditBranchForm({name:b.name,address:b.address||''}); setBranchAcctSel([]); adminLoadBranchAccts(b.id); }}>Edit</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleToggleBranch(b.id,b.is_active)}>{Number(b.is_active)===1?'Disable':'Enable'}</button>
                      <button className="btn btn-secondary btn-sm" style={{color:'var(--red)'}} onClick={() => handleDeleteBranch(b.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'locations' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom:12 }}>
              <div className="card-header"><span>New Location</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
              <div className="card-body">
                <div className="form-grid-2">
                  <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={locationForm.name} onChange={e => setLocationForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Main Warehouse" /></div>
                  <div className="form-group"><label className="form-label">Branch *</label>
                    <select className="form-control" value={locationForm.branch_id} onChange={e => setLocationForm(f=>({...f,branch_id:e.target.value}))}>
                      <option value="">Select branch</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={handleLocation}>Save Location</button>
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Branch</th><th>Status</th></tr></thead>
              <tbody>
                {locations.length === 0 ? <tr><td colSpan={3}><div className="empty-state"><p>No locations yet.</p></div></td></tr>
                : locations.map(l => (
                  <tr key={l.id}>
                    <td style={{fontWeight:600}}>{l.name}</td>
                    <td style={{color:'var(--tx3)'}}>{l.branch_name}</td>
                    <td><span className={`badge ${Number(l.is_active)===1 ? 'badge-green' : 'badge-gray'}`}>{Number(l.is_active)===1 ? 'Active' : 'Inactive'}</span></td>
                    <td style={{display:'flex',gap:4}}>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditLocation(l); setEditLocationForm({name:l.name}) }}>Edit</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleToggleLocation(l.id,l.is_active)}>{Number(l.is_active)===1?'Disable':'Enable'}</button>
                      <button className="btn btn-secondary btn-sm" style={{color:'var(--red)'}} onClick={() => handleDeleteLocation(l.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'users' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom:12 }}>
              <div className="card-header"><span>New User</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
              <div className="card-body">
                <div className="form-grid-2">
                  <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={userForm.name} onChange={e => setUserForm(f=>({...f,name:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">Email *</label><input className="form-control" type="email" value={userForm.email} onChange={e => setUserForm(f=>({...f,email:e.target.value}))} /></div>
                </div>
                <div className="form-grid-3">
                  <div className="form-group"><label className="form-label">Password *</label><input className="form-control" type="password" value={userForm.password} onChange={e => setUserForm(f=>({...f,password:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">Role *</label>
                    <select className="form-control" value={userForm.role_id} onChange={e => setUserForm(f=>({...f,role_id:e.target.value}))}>
                      <option value="">Select role</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Scope</label>
                    <select className="form-control" value={userForm.scope} onChange={e => setUserForm(f=>({...f,scope:e.target.value, branch_id: e.target.value === 'company' ? '' : f.branch_id}))}>
                      <option value="company">Company (all branches)</option>
                      <option value="branch">Branch (single branch)</option>
                    </select>
                  </div>
                  {userForm.scope === 'branch' && (
                    <div className="form-group"><label className="form-label">Branch *</label>
                      <select className="form-control" value={userForm.branch_id} onChange={e => setUserForm(f=>({...f,branch_id:e.target.value}))}>
                        <option value="">Select branch</option>
                        {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <button className="btn btn-primary" onClick={handleUser}>Create User</button>
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Scope</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {users.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No users yet.</p></div></td></tr>
                : users.map(u => (
                  <tr key={u.id}>
                    <td style={{fontWeight:600}}>{u.name}</td>
                    <td style={{color:'var(--tx3)'}}>{u.email}</td>
                    <td><span className="badge badge-blue">{u.role_name}</span></td>
                    <td><span className="badge badge-purple">{u.scope}</span></td>
                    <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{textAlign:'right', whiteSpace:'nowrap'}}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEditUser(u)} style={{marginRight:6}}>Edit</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setPwUser(u)} style={{marginRight:6}}>Password</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleToggleUser(u)}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {tab === 'roles' && <RoleManagement />}
      {tab === 'terms' && <RepairTermsTab />}
      {editUser && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div className='card' style={{width:440,padding:24,maxHeight:'85vh',overflowY:'auto'}}>
            <div className='card-header'><span>Edit User</span><button className='btn btn-secondary btn-sm' onClick={() => setEditUser(null)}>Cancel</button></div>
            <div className='card-body'>
              <div style={{fontSize:11,color:'var(--tx3)',marginBottom:12}}>{editUser.email}</div>
              <div className='form-group'><label className='form-label'>Name *</label><input className='form-control' value={editUserForm.name} onChange={e => setEditUserForm((f:any)=>({...f,name:e.target.value}))} /></div>
              <div className='form-group'><label className='form-label'>Phone</label><input className='form-control' value={editUserForm.phone} onChange={e => setEditUserForm((f:any)=>({...f,phone:e.target.value}))} /></div>
              <div className='form-group'><label className='form-label'>Role *</label>
                <select className='form-control' value={editUserForm.role_id} onChange={e => setEditUserForm((f:any)=>({...f,role_id:e.target.value}))}>
                  <option value=''>Select role</option>
                  {roles.map((r:any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className='form-group'><label className='form-label'>Scope *</label>
                <select className='form-control' value={editUserForm.scope} onChange={e => setEditUserForm((f:any)=>({...f,scope:e.target.value, branch_id: e.target.value==='company' ? '' : f.branch_id}))}>
                  <option value='company'>Company (all branches)</option>
                  <option value='branch'>Branch (single branch)</option>
                </select>
              </div>
              {editUserForm.scope === 'branch' && (
                <div className='form-group'><label className='form-label'>Branch *</label>
                  <select className='form-control' value={editUserForm.branch_id} onChange={e => setEditUserForm((f:any)=>({...f,branch_id:e.target.value}))}>
                    <option value=''>Select branch</option>
                    {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className='btn btn-secondary' onClick={() => setEditUser(null)}>Cancel</button>
                <button className='btn btn-primary' onClick={handleSaveUser}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {pwUser && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div className='card' style={{width:400,padding:24}}>
            <div className='card-header'><span>Reset Password</span><button className='btn btn-secondary btn-sm' onClick={() => { setPwUser(null); setPwValue(''); }}>Cancel</button></div>
            <div className='card-body'>
              <div style={{fontSize:11,color:'var(--tx3)',marginBottom:12}}>{pwUser.email}</div>
              <div className='form-group'><label className='form-label'>New Password *</label><input className='form-control' type='text' value={pwValue} onChange={e => setPwValue(e.target.value)} placeholder='At least 8 characters' /></div>
              <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className='btn btn-secondary' onClick={() => { setPwUser(null); setPwValue(''); }}>Cancel</button>
                <button className='btn btn-primary' onClick={handleResetPw}>Reset Password</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editBranch && (<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}><div className='card' style={{width:400,padding:24}}><div className='card-header'><span>Edit Branch</span><button className='btn btn-secondary btn-sm' onClick={() => setEditBranch(null)}>Cancel</button></div><div className='card-body'><div className='form-group'><label className='form-label'>Name</label><input className='form-control' value={editBranchForm.name||''} onChange={e => setEditBranchForm((f:any)=>({...f,name:e.target.value}))} /></div><div className='form-group'><label className='form-label'>Address</label><input className='form-control' value={editBranchForm.address||''} onChange={e => setEditBranchForm((f:any)=>({...f,address:e.target.value}))} /></div><div className='form-group'><label className='form-label'>Payment accounts for this branch</label><div style={{maxHeight:200,overflowY:'auto',border:'1px solid var(--bdr)',borderRadius:6,padding:8}}>{cashAccounts.length===0 ? <div style={{fontSize:12,color:'var(--tx3)'}}>No accounts</div> : cashAccounts.map((a:any) => (<label key={a.id} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,padding:'3px 0',cursor:'pointer'}}><input type='checkbox' checked={branchAcctSel.includes(Number(a.id))} onChange={e => setBranchAcctSel(s => e.target.checked ? [...s, Number(a.id)] : s.filter(x => x !== Number(a.id)))} />{(a.account_code? a.account_code+' - ':'')+(a.account_name||a.name||'')}</label>))}</div><div style={{fontSize:11,color:'var(--tx3)',marginTop:4}}>Tick the accounts a cashier may use at this branch. Leave all unticked to allow every account.</div></div><div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}><button className='btn btn-secondary' onClick={() => setEditBranch(null)}>Cancel</button><button className='btn btn-primary' onClick={handleSaveBranch}>Save</button></div></div></div></div>)}
      {editLocation && (<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}><div className='card' style={{width:400,padding:24}}><div className='card-header'><span>Edit Location</span><button className='btn btn-secondary btn-sm' onClick={() => setEditLocation(null)}>Cancel</button></div><div className='card-body'><div className='form-group'><label className='form-label'>Name</label><input className='form-control' value={editLocationForm.name||''} onChange={e => setEditLocationForm((f:any)=>({...f,name:e.target.value}))} /></div><div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}><button className='btn btn-secondary' onClick={() => setEditLocation(null)}>Cancel</button><button className='btn btn-primary' onClick={handleSaveLocation}>Save</button></div></div></div></div>)}
    </>
  );
};

export default Settings;